<template>
	<div>
		<b-modal
			:visible="value"
			id="article-preview-modal"
			size="xl"
			@cancel="cancel"
			@close="cancel"
			@hidden="hidden"
			class="pl-1 pr-1"
		>
			<template #modal-title>
				SWO
                <span v-if="article.is_activity">Activity</span>
                <span v-else-if="article.is_rubric">Rubric</span>
                <span v-else-if="article.is_walkthrough">Walkthrough</span>
                <span v-else> Article</span>
                Preview
			</template>
			<div class="d-flex justify-content-center">
				<!-- this is for rubrics -->
				<b-card
					header-tag="h4"
					header-text-variant="white"
					header-class="rounded-0 preview-header"
					:class="{'is-k3': is_k3}"
					class="mb-3 article-preview flex-grow-1"
					style="margin-left: 8px; margin-right: 8px;"
					v-if="article.is_rubric"
				>
					<template v-slot:header>
						<div class="d-flex align-items-center">
							<div>
								<span>Rubric</span>

								<span v-if="include_numbering && use_custom_article_number"> {{' ' + custom_article_number }}</span>
								<span v-else-if="include_numbering">{{' ' + seq_article_number }}</span>:

								{{ article.title }}
							</div>
						</div>
					</template>

					<div v-for="question in article.questions" class="mb-3">
						<div v-if="question.type == 'text_block'" class="card">
							<div v-html="text_block_body(question)" class="card-body pl-3 pr-3 text-question-text"></div>
						</div>
						<div v-if="question.type == 'media_block'" class="card">
							<div v-html="text_block_body(question)" class="card-body pl-3 pr-3 text-question-text"></div>
							<div class="p-4">
								<img
									v-if="question.content.media.type == 'image'"
									width="100%"
									:src="question.content.media.src"
								/>
								<video
									v-if="question.content.media.type == 'video'"
									width="100%"
									:poster="question.content.media.splash_url"
									controls
								>
									<source :src="question.content.media.src">
									Your browser does not support the video tag.
								</video>
								<audio
									v-if="question.content.media.type == 'audio'"
									controls
								>
									<source :src="question.content.media.src">
									Your browser does not support the audio tag.
								</audio>
							</div>
						</div>
					</div>

					<div class="rubric-table rubric-table-teacher">
						<div
							v-for="(row, row_index) in tableData"
							:key="`rubric-${rubric_type}-row-${row_index}`"
							class="rubric-row"
						>
							<div v-if="rubric_type === 'Single Point Rubric'" :class="[!row_index ? 'rubric-heading' : 'rubric-cell rubric-comments']">
								<span v-if="!row_index">
									<span class="rubric-heading-lg">Not Yet!</span>
									<br/>
									Evidence for not quite reaching the success criteria. (Suggestions for improvement)
								</span>
							</div>
							<div
								v-for="(cell, cell_index) in row.cells"
								:key="`rubric-${rubric_type}-row-${row_index}-cell-${cell_index}`"
								:class="[
									!row_index ? 'rubric-heading' : 'rubric-cell',
									rubric_type === 'Multi-Point Rubric' && !cell_index ? 'rubric-cell-multi' : '',
								]"
							>
								{{ cell.text }}
							</div>
							<div
								class="rubric-scores"
								:class="[
									!row_index ? 'rubric-heading' : 'rubric-cell'
								]"
							>
								<span v-if="!row_index">Score</span>
								<span v-else>

									/ <span v-if="rubric_type === 'Multi-Point Rubric'">{{ row.cells.length - 1 }}</span><span v-else>{{ row.score != null ? row.score : 1 }}</span>
								</span>
							</div>
							<div v-if="rubric_type === 'Single Point Rubric'" :class="[!row_index ? 'rubric-heading' : 'rubric-cell rubric-comments']">
								<span v-if="!row_index">
									<span class="rubric-heading-lg">For Sure!</span>
									<br/>
									Evidence for going beyond success criteria.
								</span>
							</div>
						</div>
						<div class="rubric-row">
							<div v-if="rubric_type === 'Single Point Rubric'"
								class="rubric-scores rubric-cell"
							>
								<span>

								</span>
							</div>
							<div
								v-for="(cell, cell_index) in tableData[0].cells"
								:key="`rubric-${rubric_type}-row-total-cell-${cell_index}`"
								:class="['rubric-cell',
									rubric_type === 'Multi-Point Rubric' && !cell_index ? 'rubric-cell-multi' : '',
								]"
							>
								<span v-if="cell_index === 0">Total</span>
							</div>
							<div
								class="rubric-scores rubric-cell"
							>
								<span v-if="rubric_type === 'Single Point Rubric'">
									/ {{ tableScoreTotal }}
								</span>
								<span v-else>
									/ {{ (tableData.length - 1) * (tableData[0].cells.length - 1) }}
								</span>
							</div>
							<div v-if="rubric_type === 'Single Point Rubric'"
								class="rubric-scores rubric-cell"
							>
								<span>

								</span>
							</div>
						</div>
					</div>
				</b-card>
				<!-- this is for walkthrough -->
				<b-card
					header-tag="h4"
					header-text-variant="white"
					header-class="rounded-0 preview-header"
					:class="{'is-k3': is_k3}"
					class="mb-3 article-preview flex-grow-1"
					style="margin-left: 8px; margin-right: 8px;"
					v-else-if="article.is_walkthrough"
				>
					<template v-slot:header>
						<div class="d-flex align-items-center">
							<div>
								<span>Walkthrough</span>

								<span v-if="include_numbering && use_custom_article_number"> {{' ' + custom_article_number }}</span>
								<span v-else-if="include_numbering">{{' ' + seq_article_number }}</span>:

								{{ article.title }}
							</div>
						</div>
					</template>
					<div>
						<carousel
							:walkthroughMedia="walkthroughMedia"
							:showBottomCarousel="true"
							:identifier="'reader-mode'"
						/>
					</div>
				</b-card>
				<!-- this is for activities -->
				<b-card
					v-else-if="article.is_activity"
					header-tag="h4"
					header-text-variant="white"
					header-class="rounded-2 preview-header"
					:class="{'is-k3': is_k3}"
					class="mb-3 article-preview flex-grow-1"
					style="margin-left: 8px; margin-right: 8px;"
				>
					<template v-slot:header>
						<div class="d-flex align-items-center">
							<div>
								<span>Activity</span>

								<span v-if="include_numbering && use_custom_article_number"> {{' ' + custom_article_number }}</span>
								<span v-else-if="include_numbering">{{' ' + seq_article_number }}</span>:
								{{ article.title }}
							</div>
						</div>
					</template>
					<div v-for="question in article.questions" class="card mb-3">
						<div v-if="question.type == 'text_block' ||
								question.type == 'media_block' ||
								question.type == 'numeric_response' ||
								question.type == 'true_false'">
							<div class="card-header bg-primary text-white">
								<span v-if="question.type == 'text_block'">Text Block</span>
								<span v-else-if="question.type == 'media_block'">Media Block</span>
								<span v-else-if="question.type == 'true_false'">True False</span>
								<span v-else-if="question.type == 'numeric_response'">Numeric Response</span>
							</div>
							<div v-html="text_block_body(question)" class="card-body pl-3 pr-3"></div>
							<div v-if="question.type == 'true_false'" class="card-text mr-2 ml-2">
								<div class="v-input__control">
									<div class="v-input__slot">
										<div data-v-258594e4="" class="full-width v-item-group theme--light" data-cy="multiple-choice-answer">
											<div data-v-258594e4="" class="row">
												<div class="d-flex flex-column pt-2 pb-2 col-sm-6 col-6">
													<div data-v-258594e4="" tabindex="0" class="card text-muted pt-2 pb-2" data-cy="student-multiple-choice-answer-0">
														<div data-v-258594e4="" class="v-card__text text-center my-auto"> True </div>
													</div>
												</div>
												<div class="d-flex flex-column pt-2 pb-2 col-sm-6 col-6">
													<div data-v-258594e4="" tabindex="0" class="card text-muted pt-2 pb-2" data-cy="student-multiple-choice-answer-0">
														<div data-v-258594e4="" class="v-card__text text-center my-auto"> False </div>
													</div>
												</div>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
						<div v-else-if="
										question.type == 'multiple_choice' ||
										question.type == 'new_multiple_choice' ||
										question.type == 'select_all_that_apply' ||
										question.type == 'new_select_all_that_apply' ||
										question.type == 'open_response' ||
										question.type == 'drawing'">
							<div class="">
								<div class="card-header bg-primary text-white">
									<span v-if="question.type == 'multiple_choice'">Multiple Choice</span>
									<span v-else-if="question.type == 'new_multiple_choice'">New Multiple Choice</span>
									<span v-else-if="question.type == 'select_all_that_apply'">Select All That Apply</span>
									<span v-else-if="question.type == 'new_select_all_that_apply'">New Select All That Apply</span>
									<span v-else-if="question.type == 'drawing'">Drawing</span>
									<span v-else>Open Response</span> Question
								</div>
								<div class="card-body">
									<div class="d-flex align-center">
										<div v-html="question_text(question)"></div>
									</div>
									<div class="card-text">
										<div class="v-input__control">
											<div class="v-input__slot">
												<div data-v-258594e4="" class="full-width v-item-group theme--light" data-cy="multiple-choice-answer">
													<div data-v-258594e4="" class="row">
														<div v-for="possible_answer in question.content.possible_answers" class="d-flex flex-column pt-2 pb-2 col-sm-6 col-6">
															<div data-v-258594e4="" tabindex="0" class="card text-muted pt-2 pb-2" data-cy="student-multiple-choice-answer-0">
																<div data-v-258594e4="" class="v-card__text text-center my-auto" v-html="possible_answer.value"></div>
															</div>
														</div>
													</div>
												</div>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
						<div v-else-if="question.type == 'new_fill_in_the_blank' || question.type == 'fill_in_the_blank'">
							<div class="">
								<div class="card-header bg-primary text-white">
									<span v-if="question.type == 'fill_in_the_blank'">Fill in The Blank</span><span v-else>New Fill in The Blank</span> Question
								</div>
								<div class="card-body">
									<div class="d-flex align-center">
										<div v-html="fib_question_text(question)"></div>
									</div>
								</div>
							</div>
						</div>
						<div v-else-if="question.type == 'labeling'">
							<div data-v-031e6d9c="" class="card">
								<div class="card-header bg-primary text-white">
									Labeling Question
								</div>
								<div data-v-e1ed8e96="" data-v-031e6d9c="" class="card-body">
									<div data-v-e1ed8e96="" class="v-card__title">
										<div data-v-e1ed8e96="" data-cy="question_text" class="d-flex align-center">
											<div v-html="question_text(question)"></div>
										</div>
									</div>

									<div data-v-e1ed8e96="" class="v-card__text overflow-auto">
										<div data-v-e1ed8e96="" class="row">
											<div data-v-e1ed8e96="" class="col col-12">
												<div data-v-e1ed8e96="" class="d-flex overflow-auto">
													<div data-v-e1ed8e96="" class="flex-grow-1 text-center">
														<div data-v-e1ed8e96="" class="image-hugger">
															<img
																data-v-e1ed8e96=""
																id="student-labeling-question-main-image"
																:src="question.content.image.src"
																data-cy="labeling-image"
																class="image"
																:style="{'width': question.content.image.width+'px', 'max-width': '100%', 'min-width': question.content.image.width+'px' }"
															>

															<div v-for="label in question.content.labels"
																data-v-e1ed8e96=""
																data-label-dot-index="0"
																class="label-answer-dot"
																:style="labeling_dot_style(question, label)"
															> </div>
														</div>
													</div>
													<div class="card text-center pr-2 pl-2">
														<h3 class="mt-3">Labels</h3>
														<div v-for="answer in possible_answers(question)" class="" style="">
															<h5 class="mt-3">
																{{ answer }}
															</h5>
														</div>
													</div>
												</div>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
						<div v-else-if="question.type == 'grouping'">
							<div class="card">
								<div class="card-header bg-primary text-white">
									Grouping Question
								</div>
								<div class="card-body">
									<div class="">
										<div class="">
											<div v-html="question_text(question)"></div>
										</div>
									</div>
									<div class="row">
										<div v-for="region in grouping_regions(question.content.items)" class="col text-center card mr-2 pt-3">
											<h5>{{ region }}</h5>
										</div>

										<div class="col text-center pt-3">
											<div v-for="item in question.content.items" v-html="item.value" class="group-option-wrapper text-center mt-1 pt-2">
											</div>
										</div>
									</div>

								</div>
							</div>
						</div>
						<div v-else-if="question.type == 'sorting'">
							<div class="card">
								<div class="card-header bg-primary text-white">
									Sorting Question
								</div>
								<div class="card-body">
									<div class="">
										<div class="">
											<div v-html="question_text(question)"></div>
										</div>
									</div>
									<div class="card p-3">
										<div v-for="(option, index) in question.content.options" class="sorting-option-wrapper text-center mt-1 pt-2">
											<span>{{ index + 1 }}. </span><span v-html="option.text"></span>
										</div>
									</div>

								</div>
							</div>
						</div>
						<div v-else-if="question.type == 'matching'">
							<div class="card">
								<div class="card-header bg-primary text-white">
									Matching Question
								</div>
								<div class="card-body">
									<div class="">
										<div class="">
											<div v-html="question.content.question_prompt"></div>
										</div>
									</div>
									<div class="card p-3">
										<div class="row">
											<div class="col-6">
												<div v-for="pair in question.content.answer_pairs" class="card text-center p3 mb-3">
													<img v-if="pair.left && pair.left.image" :src="pair.left.image" />
													<div v-else> {{ pair.left.text }} </div>
												</div>
											</div>

											<div class="col-6">
												<h4 class="text-center"> Options </h4>
												<div v-for="pair in question.content.answer_pairs" class="card text-center p3 mb-3">
													<img v-if="pair.right && pair.right.image" :src="pair.right.image" />
													<div v-else> {{ pair.right.text }} </div>
												</div>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
						<div v-else-if="question.type == 'hot_spot'">
							<div class="card">
								<div class="card-header bg-primary text-white">
									Hot Spot Question
								</div>
								<div class="card-body">
									<div class="">
										<div class="">
											<div v-html="question.content.question"></div>
										</div>
									</div>
									<div class="card p-3 text-center">
										<img :src="question.content.image.src" />
									</div>
								</div>
							</div>
						</div>
					</div>
				</b-card>
				<!-- this is for articles -->
				<b-card
					v-else
					header-tag="h4"
					header-text-variant="white"
					header-class="rounded-0 preview-header"
					:class="{'is-k3': is_k3}"
					class="mb-3 article-preview flex-grow-1"
					style="margin-left: 8px; margin-right: 8px;"
				>
					<template v-slot:header>
						<div class="d-flex align-items-center">
							<div>
								<span>Article</span>

								<span v-if="include_numbering && use_custom_article_number"> {{' ' + custom_article_number }}</span>
								<span v-else-if="include_numbering">{{' ' + seq_article_number }}</span>:

								{{ article.title }}
							</div>
						</div>
					</template>

					<div v-html="article_body()"></div>
				</b-card>
			</div>



			<template #modal-footer>
				<b-button variant="light" @click="cancel">
					Close
				</b-button>
			</template>
		</b-modal>
	</div>
</template>

<script>

import Carousel from '@/components/Carousel.vue'

const buildInfoForImg = (currentImage) => {
    const attributes = [
        'credit',
        'caption',
        'description',
    ]

    // walks the attributes for this image and extracts the ones matching the list above.
    let attributeStr = ''
    for (const attributeName of attributes) {
        const textSize = attributeName === 'credit' ? 12 : 14
        const attributeItem = currentImage.attributes.getNamedItem(attributeName)
        // skip empty attributes
        if (!attributeItem || !attributeItem.value || attributeItem.value.length === 0) {
            continue
        }

        attributeStr += `<span class="d-block" style="font-family: Poppins, -apple-system, sans-serif; font-size: ${textSize}px;">
            ${attributeItem.value}
        </span>`
    }

    return attributeStr
}

const appendImageInfo = (body) => {
    const parser = new DOMParser()
    const parsedBody = parser.parseFromString(body, 'text/html')
    const imgSelector = parsedBody.querySelectorAll('img')

    imgSelector.forEach(img => {
        const containerElement = parsedBody.createElement('div')
        const infoElement = parsedBody.createElement('div')

        infoElement.classList.add('swo-img-info-container')
        infoElement.innerHTML = buildInfoForImg(img)

        // ensure the info element aligns with the img
        const align = img.attributes.getNamedItem('align')
        if (align && align.value && (align.value === 'left' || align.value === 'right')) {
            infoElement.style.float = align.value
        }
        const width = img.attributes.getNamedItem('width');
        if (width && width.value && width.value.length !== 0) {
            infoElement.style.width = width.value
        }

        img.parentNode.insertBefore(containerElement, img)
        containerElement.append(img, infoElement)
        const element = parsedBody.createElement('div')
        element.innerHTML = buildInfoForImg(img)
        img.insertAdjacentElement('afterend', element)
    })

    return parsedBody.documentElement.innerHTML
}

export default {
	props: [
		'article',
		'is_k3',
		'value',
		'seq_article_number',
		'custom_article_number',
		'include_numbering',
		'use_custom_article_number',
		'tableData',
		'rubric_type',
		'walkthroughMedia'
	],
	components: {
		Carousel
	},
	computed: {
		tableScoreTotal() {
			let totalScore = 0;
			// loop over table data starting at i = 1 to skip header row
			for (let i = 1; i < this.tableData.length; i++) {
				const scoreToAdd = this.tableData[i].score != null ? this.tableData[i].score : 1;
				totalScore += parseInt(scoreToAdd)
			}
			return totalScore
		}
	},
	methods: {
		article_body() {
            let body = this.article.body ? this.article.body : ''
            body = this.article.body ? body.replaceAll('merlin-img', 'img') : ''
            body = this.article.body ? body.replaceAll('sbp-img', 'img') : ''
            body = appendImageInfo(body)

            return body
        },
		text_block_body(question) {
			var body = question.content.question;
			body = question.content.question ? body.replaceAll('merlin-img', 'img') : ''
            body = question.content.question ? body.replaceAll('sbp-img', 'img') : ''
			return body
		},
		question_text(question) {
			var body = question.content.question;
			body = question.content.question ? body.replaceAll('merlin-img', 'img') : ''
            body = question.content.question ? body.replaceAll('sbp-img', 'img') : ''
			return body
		},
		fib_question_text(question) {
			var body = question.content.question;
			body = question.content.question ? body.replaceAll('merlin-img', 'img') : ''
            body = question.content.question ? body.replaceAll('sbp-img', 'img') : ''
			body = body.replace(/<blank[^>]*>[\s\S]*?<\/blank>/g, ' _______ ');

			return body;
		},
		labeling_dot_style(question, label){
			let left = (label.dot.offsetX / question.content.image.width) * 100;
			let top = (label.dot.offsetY / question.content.image.height) * 100;
			left = left.toFixed(1);
			top = top.toFixed(1);
			var styling = {
				'left': left+'%',
				'top': top+'%',
			};

			return styling;
		},
		possible_answers(question){
			var possibles = [];
			_.each(question.content.labels, label => {
				if(!possibles.includes(label.text)){
					possibles.push(label.text);
				}
			})
			return possibles;
		},
		grouping_regions(items){
			var regions = [];
			_.each(items, item => {
				if(!regions.includes(item.region)){
					regions.push(item.region);
				}
			})
			return regions;
		},
		cancel (event) {
			event.preventDefault()
			this.$emit('hide')
		},
		hide() {
			this.$emit('hide')
		},
		hidden () {
            this.$emit('hide')
        }
	}
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Poppins:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900&display=swap');

.card {
	border-radius: 12px !important;
}

.text-question-text >>> p {
	font-size: 19px !important;
    font-family: Poppins,-apple-system,sans-serif !important;
    line-height: 1.3 !important;
}

>>> p {
	font-family: Poppins, -apple-system, sans-serif;
	font-size: 18px;
	line-height: 24px;
	margin-bottom: 1rem;
}
.is-k3 >>> p {
	font-family: Poppins, -apple-system, sans-serif;
	font-size: 24px;
	line-height: 32px;
	margin-top: 5px;
}

.article-preview {
	font-family: Poppins, -apple-system, sans-serif;
	border-radius: 12px !important;
	max-width: 1025px !important;
}

.article-preview >>> .carousel-container {
	font-family: Poppins, -apple-system, sans-serif;
}

.preview-header {
	background-color: #0070b9 !important;
    border-color: #0070b9 !important;
	border-top-left-radius: 12px !important;
	border-top-right-radius: 12px !important;
	font-weight: 300 !important;
	font-size: 20px !important;
	padding: 1.25rem 1.25rem;
}

>>> img {
	cursor: pointer;
	max-width: 100%;
	margin: 8px;
	height: auto;
}

>>> img[src*='#cursor-left'],
>>> img[align*='left'] {
	float: left;
	padding-right: 25px !important;
}

>>> img[src*='#cursor-right'],
>>> img[align*='right'] {
	float: right;
	padding-left: 20px !important;
}

>>> img[src*='#top'],
>>> img[src*='#bottom'],
>>> img[src*='#cursor-center']  {
	margin-right: auto !important;
	margin-left: auto !important;
	padding-top: 20px !important;
	padding-bottom: 20px !important;
	display: table;
}

>>> img[src*='#33'] {
	width: 33% !important;
}

>>> img[src*='#50'] {
	width: 50% !important;
}

>>> img[src*='#66'] {
	width: 66% !important;
}

>>> img[src*='#100'] {
	width: 100% !important;
}

.group-option-wrapper{
    width: 100%;
    background-color: #fff;
    border: 1px solid #666;
    border-radius: 50px;
    color: #000;
    font-size: 16px;
    line-height: 24px;
}

.sorting-option-wrapper{
	width: 100%;
    background-color: #fff;
    border: 1px solid #666;
    border-radius: 50px;
    color: #000;
    font-size: 16px;
    line-height: 24px;
	padding: 10px;
}

.rubric-table {
    display: table;
    border: 1px solid #666666;
    width: 100%;
    border-radius: 10px;
    margin-top: 16px;

    .rubric-row {
        display: table-row;

        .rubric-heading {
            display: table-cell;
            text-align: center;
            padding: 16px;
            border-bottom: 1px solid #666666;
            background-color: #eeeeee;

            color: var(--Gray-Dark, #666);
            font-family: Poppins;
            font-size: 12px;
            font-style: normal;
            font-weight: 700;
            line-height: normal;
            border-right: 1px solid #D9D9D9;
            vertical-align: middle;

            &:first-child {
                border-top-left-radius: 10px;
            }

            &:last-child {
                border-top-right-radius: 10px;
            }

            .rubric-heading-lg {
                font-size: 20px;
            }
        }

        .rubric-cell {
            display: table-cell;
            padding: 16px;
            font-family: Poppins;
            font-size: 12px;
            font-style: normal;
            font-weight: 400;
            line-height: normal;
            vertical-align: middle;
            border-right: 1px solid #D9D9D9;
            border-bottom: 1px solid #D9D9D9;

            &.rubric-cell-multi {
                font-family: Poppins;
                font-size: 18px;
                font-style: normal;
                font-weight: 600;
                line-height: normal;
            }

            &.rubric-scores {
                text-align: center;
                font-feature-settings: 'clig' off, 'liga' off;
                /* Big Body Bold */
                font-family: Poppins;
                font-size: 16px;
                font-style: normal;
                line-height: normal;
                width: 70px;

                .rubric-scores-graded {
                    color: var(--Black, #000);
                }
            }
        }

        & > div.rubric-heading:first-child,
        & > div.rubric-cell:first-child {
            //border-right: 1px solid #000000;
            word-break: break-word;
        }
        & > div.rubric-heading:last-child,
        & > div.rubric-cell:last-child {
            border-right: 0;
        }

        &:last-child > div.rubric-cell {
            border-bottom: 0;
        }
    }
}
.rubric-table-single,
.rubric-table-multi:last-child {
    .rubric-row {
        &:last-child > .rubric-scores .rubric-scores-graded {
            font-weight: bold;
        }
    }
}

.rubric-comments {
    min-width: 270px;
}

@media (max-width: 991px) {
	.rubric-comments {
    	min-width: 125px;
}
}

.multi-rubric-comments {
    border: 1px solid #666666;
    border-bottom-left-radius: 10px;
    border-bottom-right-radius: 10px;
    border-top: 0;
}

.sticky-save-row {
    position: sticky;
    bottom: -19px;
    background: white;
    margin: 0;
    padding-top: 12px;
}

</style>

<!-- Non-scoped styles for unmanaged elements -->
<style>
.swo-img-info-container {
    clear: both;
    text-align: left;
    margin: 0 8px;
}

#article-preview-modal .modal-title,
#article-preview-modal .modal-body,
#article-preview-modal .modal-footer {
    font-family: Poppins, -apple-system, sans-serif;
}
</style>
